package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCoinsIcon
import dev.lingvo.site.ds.LCreditCardIcon
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LSegmentBar
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.dateLong
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.state.WorkspaceSelection
import kotlin.js.Date
import kotlin.math.roundToInt
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Caption
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Tr

/**
 * What the workspace has used this period.
 *
 * The headline is what is **left**, not what has been spent, because the reader came to decide
 * whether they can start another pre-translate today. "28,412 of 30,000 used" is a fact they then
 * have to do arithmetic on; "1,588" is the answer. The used figure keeps its place in the table
 * below, where the four rows also keep included and granted apart — they reset together, and where
 * the month's allowance came from is what tells you whether the plan is the right size.
 */
@Page("/app/settings/usage")
@Layout(".components.layouts.AppLayout")
@Composable
fun UsagePage() {
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    // A retry that retries. `ErrorState` draws its button whenever `onRetry` is non-null, so
    // `{ }` produced a visible control that does nothing — on a page reached precisely when
    // the network is unreliable, whose only other exit is a full browser reload.
    var refreshKey by remember { mutableStateOf(0) }

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            PageHeader(
                t("app.settings.usage.usage", "Usage"),
                // The extractor only reaches a `PageHeader` whose first argument is still a bare
                // literal, so every subtitle on a page whose title was translated first stayed English.
                t("app.settings.usage.machine_translation_credits_for_the_current", "Machine-translation credits for the current UTC month."),
                mark = { LCoinsIcon() },
            )

            // A translator who types this URL used to get a red alert and a Retry button that could
            // never succeed: every endpoint behind these three routes calls `requireManage()`, so the
            // refusal is permanent and the retry is a lie. The sidebar hides the entry, but a URL is
            // typeable and gets pasted into chat. The answer is about their role, so it is stated as
            // one — and with no action, because retrying will not change it.
            if (workspace != null && !workspace.role.atLeast(Role.ADMIN)) {
                UsageDenied()
                return@Column
            }

            val state = loadAsync(slug, refreshKey) { slug?.let { ApiClient.workspaceBilling(it) } }
            LAsyncContent(
                state,
                rows = 2,
                loadingLabel = t("app.settings.usage.loading_usage", "Loading usage"),
                // A failed usage read cannot establish a payment outcome.
                failure = {
                    ErrorState(
                        t("app.settings.usage.usage_status_unavailable", "Usage could not be loaded. Retry to check the current recorded credit totals."),
                        title = t("app.settings.usage.we_cant_read_your_usage_right", "We can't read your usage right now"),
                        onRetry = { refreshKey++ },
                    )
                },
            ) { billing ->
                billing?.let {
                    MeterCard(it)
                    AttentionRow(it, workspace?.role)
                }
            }

            // Outside the loaded content, and that is the fix rather than the layout. These were the
            // only exits from the page and they sat inside the success branch, so a failed load left a
            // reader with a red box and nowhere to go but the browser's Back button.
            FooterLinks()
        }
    }
}

/**
 * The meter: what is left, how much of the allowance that is, and what it is doing.
 *
 * Every figure here is grouped ([formatCount]) because ungrouped five-digit credit counts are the
 * one thing on this page a reader cannot check at a glance — `28412` and `2841` differ by a
 * character, and by an order of magnitude.
 */
@Composable
internal fun MeterCard(billing: BillingOverviewDto) {
    val palette = ColorMode.current.toSitePalette()
    val used = billing.creditsUsed
    val included = billing.creditsIncluded
    val granted = billing.creditsGranted
    val ceiling = included + granted
    val remaining = billing.creditsRemaining

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            Row(Modifier.gap(Space.MD), verticalAlignment = Alignment.Bottom) {
                Span(
                    Modifier
                        .displayFont()
                        .tabularNumbers()
                        .fontWeight(700)
                        .fontSize(TypeScale.Display)
                        .lineHeight(TypeScale.DisplayLineHeight)
                        .color(palette.textPrimary)
                        .styleModifier { property("letter-spacing", "-1.1px") }
                        .toAttrs(),
                ) {
                    Text(formatCount(remaining))
                }
                Span(
                    Modifier
                        .fontSize(TypeScale.Body)
                        .fontWeight(400)
                        .color(palette.textSecondary)
                        .toAttrs(),
                ) {
                    Text(t("app.settings.usage.credits_left_this_month", "credits left this month"))
                }
            }

            // **The remainder is the trough, not a fill.** It used to be painted in `borderStrong`,
            // which is the token reserved for granted credits that have been spent — so a workspace
            // that had never been granted one still rendered a second, heavier-looking segment for
            // the part it had not spent, and the bar read as fuller than it was. Bare trough here
            // means the only ink on the bar is credit that has actually gone.
            //
            // `LSegmentBar` sizes each segment against the sum of the segments it is given, so the
            // remainder has to be passed for the proportion to be right; passing it in the trough's
            // own colour is what leaves it bare. Its 2px inter-segment gap is the separator the
            // design system requires between `accent` and `borderStrong`, which sit at only 1.70:1
            // against each other.
            val includedUsed = minOf(used, included).toDouble()
            val grantedUsed = (used - included).coerceAtLeast(0).toDouble()
            val unspent = (ceiling - used).coerceAtLeast(0).toDouble()
            // Segment names are not decoration: `LSegmentBar` hides the painted bar from assistive
            // technology and re-serves each label in the `ChartTable` beneath it, so an untranslated
            // name here is an untranslated row of the only version of this bar a screen reader gets.
            val includedLabel = t("app.settings.usage.included_used", "Included, used")
            val grantedLabel = t("app.settings.usage.granted_used", "Granted, used")
            val leftLabel = t("app.settings.usage.left", "Left")
            // **A month with nothing spent draws no bar.** With `used` at zero the only segment
            // left is the trough, so the card rendered a full-width, cell-less grey pill — 1.18:1
            // against the card in light and a hole in it in dark — that reads as a skeleton still
            // loading. It also stated nothing: "250 credits left this month" is directly above it,
            // "Nothing spent yet this month" directly below, and "Left 250" in the table under
            // that. The bar earns its place the moment there is ink to put in it.
            if (used > 0) {
                LSegmentBar(
                    segments = buildList {
                        if (includedUsed > 0) add(Triple(includedLabel, includedUsed, palette.accent))
                        if (grantedUsed > 0) add(Triple(grantedLabel, grantedUsed, palette.borderStrong))
                        if (unspent > 0) add(Triple(leftLabel, unspent, palette.surfaceSunken))
                    },
                    ariaLabel = t("app.settings.usage.credits_this_month", "Credits this month"),
                )
            }

            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                // **The position is stated once, by the figure at the top of this card.** "250
                // credits left this month." sat 100px under a tile reading "250 / credits left this
                // month" — the same fact in the same words, and a reader who compares them is
                // looking for a difference that is not there. What the tile cannot say is the
                // consequence of nothing being left, so that sentence stays and only that one does.
                if (remaining <= 0) {
                    Sentence(
                        t("app.settings.usage.none_left_this_month_pre_translate", "None left this month. Pre-translate is paused; everything else works."),
                        palette.textPrimary,
                    )
                }
                Sentence(burnRateLine(used, remaining), palette.textSecondary)
            }

            // `LDivider`, which is this exact element — full width, 1px, `border` colour — as an
            // `hr`. A rule drawn as a `div` is a rule only sighted readers can see; the semantics
            // were the whole difference and the pixels are identical.
            LDivider()

            MeterTable(billing)
        }
    }
}

/**
 * The four figures, kept as a real table because they are read against each other.
 *
 * Included and granted are separate rows for the same reason they are separate bar segments: a
 * single "allowance" row would hide that part of this month came from a top-up or a correction
 * rather than from the plan.
 *
 * **Deliberately not [LTable], which is a fair thing to ask.** That component wraps every table in
 * a scroll container carrying `role="region"` and `tabIndex(0)`, which is exactly right for the
 * wide data tables it was built for — a grid that overflows must be reachable by keyboard to be
 * scrolled at all. This is two columns and four rows and cannot overflow at any width, so adopting
 * it would buy consistent striping at the price of a tab stop on the settings page that stops at a
 * region nobody needs to scroll. The row styling here is a `th scope="row"` label against one
 * figure, which is a different shape from `LTable`'s column-headed grid.
 */
@Composable
private fun MeterTable(billing: BillingOverviewDto) {
    val palette = ColorMode.current.toSitePalette()
    val seats = billing.seats.held
    val remaining = billing.creditsRemaining

    Table(
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("border-collapse", "collapse")
                property("table-layout", "fixed")
            }
            .toAttrs(),
    ) {
        Caption(
            Modifier
                .color(palette.textMuted)
                .fontSize(TypeScale.Small)
                .fontWeight(500)
                .padding(bottom = Space.SM)
                .styleModifier {
                    property("caption-side", "top")
                    property("text-align", "start")
                }
                .toAttrs(),
        ) {
            Text(t("app.settings.usage.credits_this_period", "Credits this period"))
        }
        Tbody {
            MeterRow(
                // Server-derived, never typed: the plan's name and the seat count it is charged on
                // are the only honest way to say where the allowance came from. The count is
                // conjugated by `Intl.PluralRules` rather than by `if (n == 1)`, which is English's
                // rule and gets Polish, Arabic and Japanese wrong on a page about money.
                label = tf(
                    "app.settings.usage.included_with_plan",
                    "Included with {plan} ({seats})",
                    "plan" to billing.plan.displayName,
                    "seats" to tPlural("app.settings.usage.plan_seats", seats, "{count} seat", "{count} seats"),
                ),
                value = billing.creditsIncluded,
                zebra = true,
            )
            MeterRow(label = t("app.settings.usage.granted", "Granted"), value = billing.creditsGranted, zebra = false)
            MeterRow(
                label = tf(
                    "app.settings.usage.used_since",
                    "Used since {date}",
                    "date" to (dateLong(firstOfThisUtcMonth()) ?: t("app.settings.usage.the_first", "the 1st")),
                ),
                value = billing.creditsUsed,
                zebra = true,
            )
            MeterRow(label = t("app.settings.usage.left", "Left"), value = remaining, zebra = false, emphatic = true)
        }
    }
}

/**
 * One label-and-figure row.
 *
 * A zero renders in [dev.lingvo.site.ds.SitePalette.textSecondary] rather than in full ink: a true
 * zero is a fact, not a number to read, and giving it the same weight as a spent figure is how
 * "granted: 0" starts looking like a charge.
 */
@Composable
private fun MeterRow(label: String, value: Int, zebra: Boolean, emphatic: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    Tr(
        Modifier
            .let { if (zebra) it.backgroundColor(palette.surfaceSunken) else it }
            .let {
                if (emphatic) {
                    it.styleModifier { property("border-top", "1px solid ${palette.border}") }
                } else {
                    it
                }
            }
            .toAttrs(),
    ) {
        Th(
            Modifier
                .color(if (emphatic) palette.textPrimary else palette.textSecondary)
                .fontSize(TypeScale.BodySmall)
                .fontWeight(if (emphatic) 600 else 400)
                .padding(topBottom = Space.MD)
                .styleModifier { property("text-align", "start") }
                .toAttrs { attr("scope", "row") },
        ) {
            Text(label)
        }
        Td(
            Modifier
                .tabularNumbers()
                .color(if (value == 0 && !emphatic) palette.textSecondary else palette.textPrimary)
                .fontSize(TypeScale.Body)
                .fontWeight(if (emphatic) 600 else 500)
                .padding(topBottom = Space.MD)
                .styleModifier { property("text-align", "end") }
                .toAttrs(),
        ) {
            Text(formatCount(value))
        }
    }
}

/**
 * What needs a decision this month, or a plain sentence saying nothing does.
 *
 * Not a card, not a widget with a tick: a box that appears whether or not there is anything in it
 * teaches people to stop reading it, and this is the band that has to be believed on the month
 * something is wrong. Only the "running low" case is computable from the meter alone; the other
 * four the design system lists need server signals this route does not have.
 */
@Composable
internal fun AttentionRow(billing: BillingOverviewDto, role: Role?, daysToGo: Int = daysLeftInThisUtcMonth()) {
    val ceiling = billing.creditsIncluded + billing.creditsGranted
    val percentUsed = if (ceiling <= 0) 0 else (billing.creditsUsed * 100.0 / ceiling).toInt()
    val attention = creditAttention(billing, daysToGo)

    if (attention != CreditAttention.NONE) {
        // The word carries the severity, never the hue on its own.
        LAlert(
            if (attention == CreditAttention.EXHAUSTED) t("app.settings.usage.exhausted", "No Lingvo credits left")
            else t("app.settings.usage.running_low", "Running low"),
            if (attention == CreditAttention.EXHAUSTED) t("app.settings.usage.exhausted_help", "Lingvo-funded translation is paused until credits are available.")
            else tf(
                "app.settings.usage.percent_of_credits_gone_with_days_to_go",
                "{percent}% of this month's credits are gone and there are {days} days to go.",
                "percent" to percentUsed,
                "days" to daysToGo,
            ),
            modifier = Modifier.fillMaxWidth(),
            variant = LAlertVariant.WARNING,
        )
        CreditRecovery(role)
    } else {
        Sentence(
            t("app.settings.usage.nothing_needs_attention_this_month", "Nothing needs attention this month."),
            ColorMode.current.toSitePalette().textSecondary,
        )
    }
}

/**
 * The four exits, in the order somebody leaves by.
 *
 * The second row is the billing detail: each of those three pages used to be reachable from
 * nothing, and a page nobody can navigate to is a page nobody maintains.
 */
@Composable
private fun FooterLinks() {
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        Row(Modifier.gap(Space.MD).styleModifier { property("flex-wrap", "wrap") }) {
            Link(path = "/app/settings/usage/events") { Text(t("app.settings.usage.credit_ledger", "Credit ledger")) }
            Link(path = "/app/settings/usage/budgets") { Text(t("app.settings.usage.budgets", "Budgets")) }
            Link(path = "/app/reports/spend") { Text(t("app.settings.usage.where_it_went", "Where it went")) }
            Link(path = "/app/settings/billing") { Text(t("app.settings.usage.billing", "Billing")) }
        }
        Row(Modifier.gap(Space.MD).styleModifier { property("flex-wrap", "wrap") }) {
            Link(path = "/app/settings/billing/plans") { Text(t("app.settings.usage.plans_and_limits", "Plans and limits")) }
            Link(path = "/app/settings/billing/seats") { Text(t("app.settings.usage.seats", "Seats")) }
            Link(path = "/app/settings/billing/invoices") { Text(t("app.settings.usage.invoices", "Invoices")) }
            Link(path = "/app/insights") { Text(t("app.settings.usage.throughput_and_forecast", "Throughput and forecast")) }
        }
    }
}

/** The refusal every route in this area shares. See [UsagePage] for why it carries no retry. */
@Composable
internal fun UsageDenied() {
    LEmptyState(
        t("app.settings.usage.only_owners_and_admins_can_see", "Only owners and admins can see usage"),
        t("app.settings.usage.ask_an_owner_or_an_admin", "Ask an owner or an admin if you need the numbers."),
        Modifier.fillMaxWidth().maxWidth(640.px),
        // A closed door, not a chart with no data in it: every other refusal in the product draws
        // FORBIDDEN, and this is the one a translator reaches by typing a URL.
        LEmptyStateIcon.FORBIDDEN,
    ) {}
}

/** 15/400, the size both sentences in the meter card are set at. */
@Composable
private fun Sentence(text: String, tone: com.varabyte.kobweb.compose.ui.graphics.Color) {
    P(
        Modifier
            .tabularNumbers()
            .color(tone)
            .fontSize(TypeScale.BodySmall)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(text)
    }
}

/**
 * What the number is doing, not just what it is.
 *
 * Four conditions, and each one is a different decision. The version this replaces had four
 * *different* conditions — two of them invented ("comfortably inside this month's allowance", which
 * the page cannot know, since it does not know what the reader intends to spend it on) — and the
 * ordinary case rendered "roughly 1 days" because nothing singularised the count. A page about
 * money that cannot conjugate is a page whose arithmetic gets doubted.
 *
 * The rate is the month's spend over the days elapsed **in that month**, which is the only rate
 * this page can honestly claim to know. [daysElapsedThisUtcMonth] is fractional so that the first
 * hours of the first are a real state rather than an unreachable branch: a whole-day count from
 * `getUTCDate()` is 1-based and never below one, so the guard that was meant to suppress a wild
 * extrapolation from twenty minutes of spending could never fire.
 *
 * [elapsedDays] defaults to the real clock and is a parameter so that a test can state which day of
 * the month it is standing on: every branch below the first depends on it, and a suite that only
 * ever runs after the second of the month cannot see three of them.
 */
internal fun burnRateLine(
    used: Int,
    remaining: Int,
    elapsedDays: Double = daysElapsedThisUtcMonth(),
): String {
    // `tr`, not `t`: this is a plain function, and the catalogue is fixed before the first
    // composition anyway, so there is nothing here for a lookup to subscribe to.
    if (used == 0) return tr("app.settings.usage.nothing_spent_yet_this_month", "Nothing spent yet this month.")
    val elapsed = elapsedDays
    if (elapsed < 1.0) {
        return tr("app.settings.usage.too_early_in_the_month_to", "Too early in the month to estimate a rate.")
    }
    val perDay = used / elapsed
    val rate = formatCount(perDay.roundToInt())
    if (remaining == 0) {
        return trf(
            "app.settings.usage.about_rate_a_day_there_is",
            "About {rate} a day. There is nothing left to run down.",
            "rate" to rate,
        )
    }
    val daysLeft = (remaining / perDay).toInt()
    // Never "roughly 0 days", which reads as a rounding error rather than as a warning.
    if (daysLeft < 1) {
        return trf(
            "app.settings.usage.about_rate_a_day_so_less",
            "About {rate} a day, so less than a day of credit left at this rate.",
            "rate" to rate,
        )
    }
    // The day count is conjugated by the reader's own language. `if (daysLeft == 1)` singularised
    // English and nothing else, which is the bug this product exists to sell the fix for.
    //
    // One message, not an English prefix glued onto a translated plural: the `+` fixed the order of
    // the two halves, so "About 812 a day, so roughly 14 Tage Guthaben…" was the best any locale
    // could do, and the rate's position in the sentence was not the translator's to choose.
    return tPlural(
        "app.settings.usage.burn_rate_days_left",
        daysLeft,
        "About {rate} a day, so roughly {count} day of credit left at this rate.",
        "About {rate} a day, so roughly {count} days of credit left at this rate.",
        "rate" to rate,
    )
}

/**
 * Days into the billing month, fractional, and never a divisor of zero.
 *
 * The allowance resets at midnight UTC on the first, so the elapsed count is the day of the month
 * minus one plus the fraction of today that has passed. Fractional rather than whole because the
 * only caller has to be able to tell "half a day in" from "a day in" — see [burnRateLine].
 */
private fun daysElapsedThisUtcMonth(): Double {
    val now = Date()
    val secondsToday = now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds()
    return (now.getUTCDate() - 1) + secondsToday / 86_400.0
}

/** Whole days between now and the reset, so "there are 13 days to go" is a count, not a guess. */
private fun daysLeftInThisUtcMonth(): Int {
    val now = Date()
    val lastDay = Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate()
    return (lastDay - now.getUTCDate()).coerceAtLeast(0)
}

/** `2026-08-01`, for [dateLong] — the day the counter this page renders was last set to zero. */
private fun firstOfThisUtcMonth(): String {
    val now = Date()
    val month = (now.getUTCMonth() + 1).toString().padStart(2, '0')
    return "${now.getUTCFullYear()}-$month-01"
}
